import type {Environment} from 'relay-runtime'

import {commitCreateIssueAddBlockedByMutation} from '../mutations/create-issue-add-blocked-by-mutation'

/**
 * Helper to create a single relationship mutation as a promise
 */
function createRelationshipMutation(
  environment: Environment,
  issueId: string,
  blockingIssueId: string,
): Promise<{success: boolean; issueId: string; error?: string}> {
  return new Promise(resolve => {
    commitCreateIssueAddBlockedByMutation({
      environment,
      input: {issueId, blockingIssueId},
      onError: error => {
        // Don't report individual errors - let aggregate handler report summary
        resolve({success: false, issueId: blockingIssueId, error: error.message})
      },
      onCompleted: response => {
        // Validation errors return addBlockedBy: null with onCompleted still called
        resolve({success: response.addBlockedBy !== null, issueId: blockingIssueId})
      },
    })
  })
}

/**
 * Helper to create multiple relationship mutations and throw errors
 */
async function createRelationships(
  environment: Environment,
  issueIds: Array<{issueId: string; blockingIssueId: string}>,
  relationshipType: 'blocked-by' | 'blocking',
): Promise<void> {
  const results = await Promise.allSettled(
    issueIds.map(({issueId, blockingIssueId}) => createRelationshipMutation(environment, issueId, blockingIssueId)),
  )

  const failedCount = results.filter(r => r.status === 'fulfilled' && !r.value.success).length
  if (failedCount > 0) {
    // Throw error in setTimeout so it bubbles to document and gets reported naturally
    setTimeout(() => {
      throw new Error(`Failed to add ${failedCount} of ${issueIds.length} ${relationshipType} relationship(s)`)
    })
  }
}

/**
 * Create issue relationships after creation (fire-and-forget).
 *
 * Failures are reported but don't block issue creation success.
 * Matches project assignment pattern. This will be replaced when createIssue
 * mutation supports blocking/blockedBy parameters (tracked in github/github#412771).
 */
export async function handleIssueRelationships(
  environment: Environment,
  newIssueId: string,
  blockedByIssueIds: string[],
  blockingIssueIds: string[],
): Promise<void> {
  const promises: Array<Promise<void>> = []

  if (blockedByIssueIds.length > 0) {
    promises.push(
      createRelationships(
        environment,
        blockedByIssueIds.map(blockingIssueId => ({issueId: newIssueId, blockingIssueId})),
        'blocked-by',
      ),
    )
  }

  if (blockingIssueIds.length > 0) {
    promises.push(
      createRelationships(
        environment,
        blockingIssueIds.map(blockedIssueId => ({issueId: blockedIssueId, blockingIssueId: newIssueId})),
        'blocking',
      ),
    )
  }

  await Promise.all(promises)
}
